import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface FailedSubmission {
  id: string;
  externalSubmissionId: string | null;
  formId: string | null;
  provider: 'jotform' | 'typeform';
  rawPayload: Record<string, unknown>;
  errorType: 'missing_group_id' | 'group_not_found' | 'missing_email' | 'processing_error' | 'validation_error';
  errorMessage: string;
  errorStack: string | null;
  attemptCount: string;
  status: 'pending' | 'resolved' | 'ignored';
  resolvedSubmissionId: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedSubmission?: {
    id: string;
    registrationId: string;
    groupId: string;
    registration?: {
      id: string;
      email: string;
    };
  };
}

export interface FailedSubmissionStatistic {
  status: 'pending' | 'resolved' | 'ignored';
  errorType: 'missing_group_id' | 'group_not_found' | 'missing_email' | 'processing_error' | 'validation_error';
  count: number;
}

export interface FailedSubmissionsFilters {
  status?: 'pending' | 'resolved' | 'ignored';
  errorType?: string;
}

/**
 * Fetch every API page before publishing the complete client-side table data.
 */
export async function fetchFailedSubmissions(filters?: FailedSubmissionsFilters): Promise<FailedSubmission[]> {
  const pageSize = 100;
  const queryParams = new URLSearchParams();
  if (filters?.status) queryParams.set('status', filters.status);
  if (filters?.errorType) queryParams.set('errorType', filters.errorType);
  queryParams.set('limit', String(pageSize));

  const submissions: FailedSubmission[] = [];
  for (let offset = 0; ; offset += pageSize) {
    queryParams.set('offset', String(offset));
    const page = await api.get<FailedSubmission[]>(`/failed-submissions?${queryParams.toString()}`);
    submissions.push(...page);
    if (page.length < pageSize) return submissions;
  }
}

/**
 * Hook to fetch all failed submissions with optional filters
 */
export function useFailedSubmissions(filters?: FailedSubmissionsFilters) {
  return useQuery({
    queryKey: ['failed-submissions', filters],
    queryFn: () => fetchFailedSubmissions(filters),
  });
}

/**
 * Hook to fetch a single failed submission by ID
 */
export function useFailedSubmission(id: string) {
  return useQuery({
    queryKey: ['failed-submissions', id],
    queryFn: () => api.get<FailedSubmission>(`/failed-submissions/${id}`),
    enabled: !!id,
  });
}

/**
 * Hook to fetch statistics about failed submissions
 */
export function useFailedSubmissionsStatistics() {
  return useQuery({
    queryKey: ['failed-submissions', 'statistics'],
    queryFn: () => api.get<FailedSubmissionStatistic[]>('/failed-submissions/statistics'),
  });
}

/**
 * Hook to reprocess a failed submission
 */
export function useReprocessFailedSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      overridePayload,
      resolvedBy,
    }: {
      id: string;
      overridePayload?: Record<string, unknown>;
      resolvedBy?: string;
    }) =>
      api.post<{ success: boolean; submissionId?: string; registrationId?: string }>(
        `/failed-submissions/${id}/reprocess`,
        {
          overridePayload,
          resolvedBy,
        }
      ),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['failed-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['failed-submissions', id] });
      toast.success('Inzending succesvol opnieuw verwerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij opnieuw verwerken: ${error.message}`);
    },
  });
}

/**
 * Hook to update notes on a failed submission
 */
export function useUpdateFailedSubmissionNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      api.patch<FailedSubmission>(`/failed-submissions/${id}/notes`, { notes }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['failed-submissions', id] });
      queryClient.invalidateQueries({ queryKey: ['failed-submissions'] });
      toast.success('Notities bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bijwerken notities: ${error.message}`);
    },
  });
}

/**
 * Hook to ignore a failed submission
 */
export function useIgnoreFailedSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      api.post<FailedSubmission>(`/failed-submissions/${id}/ignore`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['failed-submissions'] });
      toast.success('Inzending gemarkeerd als genegeerd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij negeren inzending: ${error.message}`);
    },
  });
}

/**
 * Hook to bulk reprocess failed submissions
 */
export function useBulkReprocessFailedSubmissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, resolvedBy }: { ids: string[]; resolvedBy?: string }) =>
      api.post<{ success: boolean; successes: number; failures: number; total: number }>(
        '/failed-submissions/bulk-reprocess',
        {
          ids,
          resolvedBy,
        }
      ),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['failed-submissions'] });
      toast.success(`${data.successes} van ${data.total} inzendingen succesvol verwerkt`);
      if (data.failures > 0) {
        toast.warning(`${data.failures} inzendingen konden niet worden verwerkt`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bulk verwerken: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a failed submission
 */
export function useDeleteFailedSubmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/failed-submissions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['failed-submissions'] });
      toast.success('Inzending verwijderd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij verwijderen inzending: ${error.message}`);
    },
  });
}
